/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/InboxEmailRows.tsx
 * FILE_ID: EMAIL-INBOX-EMAIL-ROWS-001
 * PURPOSE: Render loaded Inbox rows, workflow frames, and group headers.
 */

import { AllocateChip,AllocationChip } from '@quotech/kanban-host'
import { EmailRow,formatRecipientList } from '@quotech/ui'
import { ChevronDown,ChevronRight,Mail } from 'lucide-react'
import type { MouseEvent } from 'react'
import { CardAiTag } from '../../components/CardAiTag'
import { CardJunkControl } from '../../components/CardJunkControl'
import { CardVisibilityBadge } from '../../components/CardVisibilityBadge'
import { CategoryChip } from '../../components/CategoryChip'
import { MailboxAttributionChip,RelationChip,ResurfaceChip } from '../../components/RowAttributionChips'
import { SuggestedJobChip } from '../../components/SuggestedJobChip'
import { WorkflowRoleChip } from '../../components/WorkflowRoleChip'
import type { Email } from '../../hooks/useEmails'
import { ReferToOrgAction,SuggestedOrgChip } from '../../referrals'
import { resolveCardAiState } from '../../lib/cardAiState'
import { getCategoryConfig } from '../../lib/categoryConfig'
import { boardFrameColor,jobFrameColor,jobPillClassFor } from '../../lib/colorPalettes'
import { DATE_BUCKET_LABELS,dateBucketOf } from '../../lib/dateBuckets'
import { mailboxGroupKeyOf,mailboxGroupLabelOf } from '../../lib/dedup'
import { buildKanbanBoardLink,buildKanbanCardLink } from '../../lib/kanbanDeepLink'
import { companyLabelFromAddress,domainOf,applyCompanyFilter } from './helpers'
import type { InboxPageController } from './useInboxPageController'

export function InboxEmailRows({
  vm,
  handleContextMenu,
}: {
  vm: InboxPageController
  handleContextMenu: (event: MouseEvent, email: Email) => void
}) {
  const {
    isOutgoingFolder,
    navigate,
    kanbanApi,
    customCategories,
    kanbanOrgId,
    parsedDebouncedSearch,
    setFromAddressContains,
    setCompanyDomain,
    groupByMailbox,
    sortMode,
    collapsedMailboxes,
    toggleMailboxGroup,
    selectedEmailId,
    setSelectedEmailId,
    selectedIds,
    refetch,
    refetchPreservingScroll,
    updateEmailLocal,
    jobActionId,
    handleConfirmJob,
    handleRejectJob,
    allocActionId,
    handleConfirmAllocation,
    handleRejectAllocation,
    suggestionActionId,
    handleToggleStar,
    handleEmailClick,
    handleToggleSelect,
    handleEmailResolved,
    binningId,
    handleBinRow,
    archivingId,
    handleArchiveRow,
    unarchivingId,
    handleUnarchiveRow,
    restoringId,
    handleRestoreRow,
    readTogglingId,
    handleToggleReadRow,
    currentUserId,
    currentUserEmail,
    hasMultipleMailboxes,
    dedupedEmails,
    mailboxGroupCounts,
    suggestionsMap,
    dismissSuggestion,
    handleConfirmSuggestion,
    handleRejectSuggestion,
  } = vm
  return dedupedEmails.map((email, index) => {
              const catKey = email.category || 'uncategorized'
              const catCfg = getCategoryConfig(catKey, customCategories)
              const CatIcon = catCfg.icon

              const dotIsMuted = catKey === 'general' || catKey === 'uncategorized'

              const showInlineChip = !dotIsMuted

              const suggested = email.suggested_job ?? suggestionsMap.get(email.id)
              const suggestedFramed =
                !!suggested && !email.job_id && !email.allocation_summary

              const cardAi = resolveCardAiState({
                allocation_summary: email.allocation_summary,
                category: email.category,
                category_locked: email.category_locked,
                suggested_job: suggested ? { job_id: suggested.job_id } : null,
              })

              const rowFramed =
                !!email.allocation_summary || !!email.job_id || suggestedFramed

              const mailboxChip = (
                <MailboxAttributionChip
                  email={email}
                  currentUserEmail={currentUserEmail}
                  currentUserId={currentUserId}
                  neutral={rowFramed}
                  showOwnMailbox={hasMultipleMailboxes}
                />
              )
              const relationChip = (
                <>
                  <RelationChip email={email} />
                  <ResurfaceChip email={email} />
                </>
              )
              const row = (
                <EmailRow
                  email={email}
                  highlightQuery={parsedDebouncedSearch.freeText || undefined}
                  // Top visible card keeps disposition + board-chip chrome open
                  // at rest so the first option set is never hover-gated (#452).
                  forceActionsExpanded={index === 0}

                  peerName={
                    isOutgoingFolder
                      ? formatRecipientList(email.to_addresses, { max: 2 }) ||
                        undefined
                      : undefined
                  }
                  peerPrefix={isOutgoingFolder ? 'To: ' : undefined}
                  category={{
                    icon: CatIcon,
                    dot: catCfg.dot,
                    color: catCfg.hex,
                    label: catCfg.label,
                    dotIsMuted,
                  }}
                  isSelected={selectedEmailId === email.id}
                  isChecked={selectedIds.has(email.id)}
                  anySelected={selectedIds.size > 0}
                  onSelect={(event) => { void handleEmailClick(email, event) }}
                  onToggleSelect={(e) => handleToggleSelect(email.id, e)}
                  onToggleStar={(e) => handleToggleStar(email, e)}
                  onContextMenu={(e) => handleContextMenu(e, email)}
                  onBin={
                    email.status === 'binned'
                      ? undefined
                      : () => {
                          void handleBinRow(email)
                        }
                  }
                  isBinning={binningId === email.id}
                  onRestore={
                    email.status === 'binned'
                      ? () => {
                          void handleRestoreRow(email)
                        }
                      : undefined
                  }
                  isRestoring={restoringId === email.id}
                  onArchive={
                    email.is_archived || email.status === 'binned'
                      ? undefined
                      : () => {
                          void handleArchiveRow(email)
                        }
                  }
                  isArchiving={archivingId === email.id}
                  onUnarchive={
                    email.is_archived && email.status !== 'binned'
                      ? () => {
                          void handleUnarchiveRow(email)
                        }
                      : undefined
                  }
                  isUnarchiving={unarchivingId === email.id}
                  onToggleRead={() => {
                    void handleToggleReadRow(email)
                  }}
                  isTogglingRead={readTogglingId === email.id}
                  mailboxChip={mailboxChip}
                  relationChip={relationChip}

                  roleChip={<WorkflowRoleChip email={email} />}
                  aiTag={<CardAiTag state={cardAi.ai} />}
                  visibilityBadge={
                    /* Compact (lock-glyph only) in the list — the leading AI
                       pip already names private vs on-board; the full pill
                       lived on its own line and cluttered the row. The full
                       label stays in the reader's filing widget. */
                    <CardVisibilityBadge visibility={cardAi.visibility} compact />
                  }
                  junkControl={
                    // Binned rows are already binned: "Mark junk" would
                    // re-bin them. Restore (onRestore above) is the verb there.
                    isOutgoingFolder || email.status === 'binned' ? undefined : (
                      <CardJunkControl
                        email={email}
                        onRefetch={refetch}
                        onResolved={handleEmailResolved}
                        compact
                      />
                    )
                  }
                  onFilterSender={() => {

                    setCompanyDomain('')
                    setFromAddressContains(email.from_address || '')
                    setSelectedEmailId(null)
                  }}
                  onFilterCompany={

                    email.company_label || companyLabelFromAddress(email.from_address)
                      ? () => {
                          applyCompanyFilter({
                            domain: email.company_domain || domainOf(email.from_address),
                            label:
                              email.company_label ||
                              companyLabelFromAddress(email.from_address) ||
                              undefined,
                            setFromAddressContains,
                            setCompanyDomain,
                            setSelectedEmailId,
                          })
                        }
                      : undefined
                  }
                  companyLabel={
                    email.company_label ||
                    companyLabelFromAddress(email.from_address) ||
                    undefined
                  }
                  jobPillClass={
                    email.job_id ? jobPillClassFor(email.job_id) : undefined
                  }
                  allocationFrame={
                    /* Board-first frame, job fallback: the row wears its
                       Kanban board's colour as a rounded rail + tint when
                       allocated, so the inbox reads as workflow clusters.
                       Board over lane by design - the board is the
                       workflow everything flows through; the lane (stage)
                       still shows in the interactive AllocationChip + the
                       hover title. When an email isn't on a board yet but
                       IS linked to a job, we fall back to a job-coloured
                       spine so most of the inbox still frames as workflow
                       (board wins whenever both exist). */
                    email.allocation_summary
                      ? {
                          kind: 'board' as const,
                          color: boardFrameColor(
                            email.allocation_summary.board_color,
                            email.allocation_summary.board_id,
                          ),

                          label: email.allocation_summary.parent_board_name
                            ? `${email.allocation_summary.parent_board_name} › ${email.allocation_summary.board_name}`
                            : email.allocation_summary.board_name,
                          title: email.allocation_summary.parent_board_name
                            ? `${email.allocation_summary.parent_board_name} › ${email.allocation_summary.board_name} · ${email.allocation_summary.lane_name}`
                            : `${email.allocation_summary.board_name} · ${email.allocation_summary.lane_name}`,

                          pending: email.allocation_summary.needs_review === true,

                          href:
                            buildKanbanBoardLink(email.allocation_summary) ??
                            undefined,
                        }
                      : email.job_id
                        ? {
                            kind: 'job' as const,
                            color: jobFrameColor(email.job_id),
                            label: email.job_name || `Job #${email.job_id}`,
                            title: email.job_name
                              ? `Job · ${email.job_name}`
                              : `Job #${email.job_id}`,
                          }
                        : suggestedFramed
                          ? {
                              kind: 'suggested' as const,
                              color: jobFrameColor(suggested!.job_id),
                              label: suggested!.job_label,
                              title: `AI suggestion · ${suggested!.job_label}`,
                              pending: true,
                            }
                          : null
                  }
                  onConfirmJob={() => handleConfirmJob(email.id)}
                  isConfirmingJob={jobActionId === email.id}
                  onRejectJob={() => handleRejectJob(email.id)}
                  isRejectingJob={jobActionId === email.id}
                  onConfirmAllocation={
                    email.allocation_summary
                      ? () =>
                          handleConfirmAllocation(
                            email.id,
                            email.allocation_summary!.allocation_id,
                          )
                      : undefined
                  }
                  onRejectAllocation={
                    email.allocation_summary
                      ? () =>
                          handleRejectAllocation(
                            email.id,
                            email.allocation_summary!.allocation_id,
                            email.allocation_summary,
                          )
                      : undefined
                  }
                  isAllocationBusy={allocActionId === email.id}
                  onConfirmSuggestion={
                    suggestedFramed
                      ? () =>
                          handleConfirmSuggestion(
                            email.id,
                            suggested!.job_id,
                            suggested!.job_label,
                          )
                      : undefined
                  }
                  onRejectSuggestion={
                    suggestedFramed
                      ? () =>
                          handleRejectSuggestion(
                            email.id,
                            suggested!.job_id,
                            suggested!.job_label,
                          )
                      : undefined
                  }
                  isSuggestionBusy={suggestionActionId === email.id}
                  categoryChip={
                    <div className="flex flex-wrap items-center gap-1">
                      {showInlineChip && (
                        <CategoryChip
                          emailId={email.id}
                          category={email.category}
                          categoryLocked={email.category_locked}
                          pillClass={catCfg.pill}
                          icon={CatIcon}
                          label={catCfg.label}
                          size="sm"
                          onChanged={() => { void refetch() }}
                        />
                      )}
                      <SuggestedOrgChip
                        emailId={email.id}
                        suggested={email.suggested_org}
                        onChanged={() => { void refetch() }}
                      />
                      <ReferToOrgAction
                        emailId={email.id}
                        onChanged={() => { void refetch() }}
                      />
                    </div>
                  }
                  allocationChip={
                    /* Module C — render the Kanban allocation chip
                       side-by-side with the category chip. The chip
                       owns its own dropdown (change lane, revoke,
                       AI tick/cross) so we don't have to thread that
                       state through the page. */
                    email.allocation_summary ? (
                      <AllocationChip
                        emailId={email.id}
                        summary={email.allocation_summary}
                        api={kanbanApi}
                        orgId={kanbanOrgId}
                        onOpenCard={() => {
                          const link = buildKanbanCardLink(email.allocation_summary)
                          if (link) navigate(link)
                        }}
                        onChanged={() => {
                          void refetchPreservingScroll()
                        }}

                        compact
                      />
                    ) : null
                  }
                  allocateChip={
                    /* "Add to board" for an UNALLOCATED row — the inline
                       counterpart to AllocationChip (which only handles the
                       already-allocated case). Restores the board-allocation
                       affordance to every personal/un-triaged email straight
                       from the list (operator 2026-06-21: "I can't see the
                       kanban board allocation … what happened to AI board
                       allocations"). EmailRow only surfaces it when the row
                       has no work chip, so a framed row never shows both -
                       but gate here too so we don't mount the popover for
                       rows that already wear a frame. */
                    !rowFramed ? (
                      <AllocateChip
                        emailId={email.id}
                        api={kanbanApi}
                        orgId={kanbanOrgId}
                        label="Board"
                        onAllocated={(summary) => {
                          // BUG-2026-07-21-INBOX-SCROLL-RESET-ON-ALLOCATE
                          if (summary) {
                            updateEmailLocal(email.id, {
                              allocation_summary: summary,
                            })
                            return
                          }
                          void refetchPreservingScroll()
                        }}
                      />
                    ) : null
                  }
                  suggestedJobChip={
                    /* MR-H — restore the suggested-job chip that was
                       authored against the pre-K3c canonical (sam-only,
                       never reached production tenants). The chip
                       surfaces ONLY on rows where the BE attached a
                       `suggested_job` block AND the row isn't
                       already linked to a job.
                       BUG-2026-05-24-INBOX-LOAD-SUGGESTIONS-CRIT-PATH
                       (this MR): the chip used to come exclusively
                       from the list response via
                       ``?include_suggestions=true``. That parameter
                       added ~22 s of matcher cost to every inbox
                       fetch on prod. Now we fall back to the lazy
                       ``useInboxSuggestions`` map for any row whose
                       list-response field is empty - gives the
                       same chip without the critical-path cost.
                       The ``email.suggested_job`` branch stays as a
                       safety net in case a stray caller (or a
                       backwards-compat list response) still ships
                       the field. Whichever resolves first wins.
                       The chip handles its own accept / reject
                       affordances; the refetch callback re-pulls
                       the list so a newly-linked row drops
                       `suggested_job` and the chip disappears. */
                    (() => {

                      return suggested && !email.job_id && !suggestedFramed ? (
                        <SuggestedJobChip
                          emailId={email.id}
                          suggested={suggested}
                          onConfirmed={() => {
                            void refetchPreservingScroll()
                          }}
                          onDismissed={() => {

                            dismissSuggestion(email.id)
                          }}
                        />
                      ) : null
                    })()
                  }
                />
              )

              const groupKey = groupByMailbox ? mailboxGroupKeyOf(email) : ''
              const isGroupCollapsed = groupByMailbox && collapsedMailboxes.has(groupKey)
              const showGroupHeader =
                groupByMailbox &&
                (index === 0 ||
                  groupKey !== mailboxGroupKeyOf(dedupedEmails[index - 1]))

              const dateBucketed = !groupByMailbox && sortMode === 'newest'
              const rowBucket = dateBucketed ? dateBucketOf(email.received_at) : null
              const showDateHeader =
                dateBucketed &&
                (index === 0 ||
                  rowBucket !== dateBucketOf(dedupedEmails[index - 1].received_at))
              const dateHeader = showDateHeader && rowBucket ? (
                <div
                  key={`datehdr-${rowBucket}`}
                  data-testid="inbox-date-bucket-header"
                  className="sticky top-0 z-10 flex items-center gap-2 px-3 py-1 bg-muted/80 backdrop-blur-sm border-y border-border/60 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  {DATE_BUCKET_LABELS[rowBucket]}
                </div>
              ) : null
              const rowNode = (
                <div
                  key={email.id}
                  className="animate-in fade-in-0 slide-in-from-top-2 duration-300 motion-reduce:animate-none"
                  style={{
                    animationDelay: `${Math.min(index, 12) * 22}ms`,
                    animationFillMode: 'both',
                    contentVisibility: 'auto',
                    containIntrinsicSize: 'auto 96px',
                  }}
                >
                  {row}
                </div>
              )

              if (!showGroupHeader) {
                if (isGroupCollapsed) return null
                return dateHeader ? [dateHeader, rowNode] : rowNode
              }

              const groupCount = mailboxGroupCounts[groupKey] || 0
              const header = (
                <button
                  type="button"
                  key={`grphdr-${groupKey}`}
                  onClick={() => toggleMailboxGroup(groupKey)}
                  aria-expanded={!isGroupCollapsed}
                  className="sticky top-0 z-10 w-full flex items-center gap-2 px-3 py-1 bg-muted/80 backdrop-blur-sm border-y border-border/60 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted transition-colors"
                  title={isGroupCollapsed ? 'Expand mailbox' : 'Collapse mailbox'}
                >
                  {isGroupCollapsed
                    ? <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
                    : <ChevronDown className="h-3 w-3 shrink-0" aria-hidden />}
                  <Mail className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="truncate normal-case">{mailboxGroupLabelOf(email)}</span>
                  <span className="ml-auto shrink-0 tabular-nums normal-case font-normal text-muted-foreground/80">
                    {groupCount}
                  </span>
                </button>
              )
              return isGroupCollapsed ? [header] : [header, rowNode]
            })
}
